import { type ReactNode, useRef } from 'react';

import { RouteLink } from '../studies/RouteLink.tsx';
import { type HeaderAccount, HeaderMenu } from './HeaderMenu.tsx';
import { usePublishedHeight } from './use-published-height.ts';

export interface AppHeaderProps {
  /**
   * What the band starts with: the study's title on a study, everywhere else the wordmark, which
   * links to the list of studies.
   */
  leading?: ReactNode;
  /** Names the `…` menu for a screen reader. */
  menuLabel?: string;
  /** This screen's own items in the `…` menu, above the account section. */
  menuItems?: ReactNode;
  account: HeaderAccount;
  /** Drawn under the band's row, inside the band: the step rail on a study. */
  children?: ReactNode;
}

/**
 * The green band at the top of every signed-in screen.
 *
 * One component so every screen has the same band at the same height: `--app-header-h` is its
 * height wherever it is drawn, with or without the rail under its row, so moving between the list,
 * a study and the account screen never moves the page under the band. It is a minimum, so text
 * enlarged past what it was measured at grows the band rather than spilling out of it, so its drawn
 * height is published as `--app-header-height` for whatever is fixed beneath it.
 */
export const AppHeader = ({
  leading,
  menuLabel = 'Account',
  menuItems,
  account,
  children,
}: AppHeaderProps) => {
  const band = useRef<HTMLElement>(null);
  usePublishedHeight(band, '--app-header-height');

  return (
    <header ref={band} className="app-header" data-test-id="app-header">
      <div className="app-header-row">
        <div className="app-header-leading">
          {leading === undefined ? (
            <RouteLink
              to={{ name: 'studies' }}
              onNavigate={account.onNavigate}
              testId="app-header-wordmark"
            >
              <span className="wordmark">Sigora</span>
            </RouteLink>
          ) : (
            leading
          )}
        </div>
        <HeaderMenu label={menuLabel} items={menuItems} account={account} />
      </div>
      {children}
    </header>
  );
};
